// ============================================================
// UserProfileModal.jsx — نسخه نهایی و کامل
// مودال پروفایل کاربر در چت گروهی
// - برای دانش‌آموز: آزمون‌ها، چالش‌ها، کلاس‌ها، ویدیوها، سفر یادگیری
// - برای معلم: آزمون‌های ساخته‌شده، کلاس‌های برگزارشده، محتوای منتشرشده
// ============================================================

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useExams } from '../components/Exams/ExamContext.jsx';
import { engine } from '../core/books/storage.js';
import dataEngine from '../core/books/data.js';
import { COURSE_DETAILS } from '../pages/CourseDetail.jsx';
import {
  normalizeGradeId, normalizeFieldId, getGrade, getField,
  getSubjectTitle, needsField as curriculumNeedsField,
} from '../core/education/curriculum.js';
import './UserProfileModal.css';

// ============================================================
// CONSTANTS
// ============================================================
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const DEFAULT_AVATAR = '/avatars/avatar34.png';
const ONLINE_THRESHOLD_MS = 45000;
const CACHE_TTL_MS = 60000;

// اژدها/لیگ‌ها — کپی سبک از MedalsPage (منبع واحد)
const DRAGONS = [
  { id: 200, name: 'تخم اژدها', emoji: '🥚', image: '',              minScore: 0,     c1: '#fbbf24', c2: '#d97706', glow: 'rgba(251,191,36,0.65)' },
  { id: 201, name: 'تازه کار',   emoji: '🦎', image: '/medals/1.png', minScore: 10,    c1: '#24fbd7', c2: '#06cbd9', glow: 'rgba(36,244,251,0.65)' },
  { id: 202, name: 'ماجراجو',   emoji: '🦖', image: '/medals/2.png', minScore: 100,   c1: '#84cc16', c2: '#65a30d', glow: 'rgba(132,204,22,0.65)' },
  { id: 203, name: 'حرفه ای',    emoji: '🐲', image: '/medals/3.png', minScore: 500,   c1: '#22d3ee', c2: '#0891b2', glow: 'rgba(34,211,238,0.65)' },
  { id: 204, name: 'قهرمان',    emoji: '🐦‍🔥', image: '/medals/4.png', minScore: 1000,  c1: '#f97316', c2: '#dc2626', glow: 'rgba(249,115,22,0.65)' },
  { id: 205, name: 'افسانه',    emoji: '🐉', image: '/medals/5.png', minScore: 2000,  c1: '#818cf8', c2: '#6366f1', glow: 'rgba(129,140,248,0.65)' },
  { id: 206, name: 'استاد',      emoji: '🔱', image: '/medals/6.png', minScore: 3500,  c1: '#bae6fd', c2: '#0284c7', glow: 'rgba(186,230,253,0.65)' },
  { id: 207, name: 'استاد بزرگ', emoji: '🔥', image: '/medals/7.png', minScore: 6000,  c1: '#fde047', c2: '#eab308', glow: 'rgba(253,224,71,0.65)' },
  { id: 208, name: 'اسطوره',    emoji: '👑', image: '/medals/8.png', minScore: 10000, c1: '#e879f9', c2: '#a855f7', glow: 'rgba(232,121,249,0.65)' },
];

const SUBJECT_EMOJI = {
  riazi: '📐', farsi: '📖', olum: '🔬', ghoran: '🕌', hedye: '🌙',
  motaleat: '🌍', tafakor: '🧠', kar: '⚙️', payam: '🕊️', farhang: '🎨',
  arabi: '📜', english: '🌐', din: '🕌', hendese: '📏', hesaban: '🧮',
  amar_ehtemal: '📊', riazi_gosaste: '🔢', fizik: '⚡', shimi: '⚗️',
  zist: '🧬', riazi_amar: '📈', olon_fonon: '📚', tarikh: '🏛️',
  joghrafia: '🗺️', jamee: '👥', eqtesad: '💹', manteq: '🤔',
  ravanshenasi: '🧠', falsafe: '💭', hoghough: '⚖️', tarikh_adabiat: '📚',
};

const SUBJECT_COLORS = [
  '#8b78ea', '#22d3ee', '#ec4899', '#fbbf24', '#22c55e',
  '#f97316', '#6366f1', '#14b8a6', '#84cc16', '#e879f9',
];

// ============================================================
// HELPERS
// ============================================================
const idsEqual = (a, b) => {
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const safeObj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

const resolveAvatarUrl = (raw) => {
  if (!raw) return DEFAULT_AVATAR;
  const s = String(raw).trim();
  if (!s) return DEFAULT_AVATAR;
  if (s.startsWith('/') || s.startsWith('http')) return s;
  return DEFAULT_AVATAR;
};

const colorForSubject = (subject, idx = 0) => {
  if (!subject) return SUBJECT_COLORS[idx % SUBJECT_COLORS.length];
  let hash = 0;
  for (let i = 0; i < subject.length; i++) hash = (hash * 31 + subject.charCodeAt(i)) >>> 0;
  return SUBJECT_COLORS[hash % SUBJECT_COLORS.length];
};

const getDragonForXp = (xp) => {
  const score = Number(xp) || 0;
  let idx = 0;
  DRAGONS.forEach((d, i) => { if (score >= d.minScore) idx = i; });
  return {
    dragon: DRAGONS[idx],
    nextDragon: DRAGONS[idx + 1] || null,
    isMax: idx === DRAGONS.length - 1,
  };
};

const fa = (n) => String(n ?? '').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

const getUserDisplayName = (u) => {
  if (!u) return 'کاربر';
  if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`.trim();
  if (u.name) return u.name;
  if (u.fullName) return u.fullName;
  if (u.username) return u.username;
  if (u.firstName) return u.firstName;
  if (u.lastName) return u.lastName;
  return 'کاربر';
};

const isOnline = (userId, presenceMap) => {
  const lastSeen = safeObj(presenceMap)[userId] || 0;
  if (!lastSeen) return false;
  return Date.now() - lastSeen < ONLINE_THRESHOLD_MS;
};

const formatLastSeen = (userId, presenceMap) => {
  const lastSeen = safeObj(presenceMap)[userId] || 0;
  if (!lastSeen) return 'آخرین بازدید نامشخص';
  const diff = Date.now() - lastSeen;
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);
  if (sec < 60) return 'همین الان آنلاین بود';
  if (min < 60) return `${fa(min)} دقیقه پیش`;
  if (hour < 24) return `${fa(hour)} ساعت پیش`;
  if (day < 30) return `${fa(day)} روز پیش`;
  return `${fa(Math.floor(day / 30))} ماه پیش`;
};

// ============================================================
// CACHE (per userId، TTL=60s)
// ============================================================
const profileCache = new Map();

const getCached = (userId) => {
  const entry = profileCache.get(String(userId));
  if (!entry) return null;
  if (Date.now() - entry.at > CACHE_TTL_MS) {
    profileCache.delete(String(userId));
    return null;
  }
  return entry.data;
};

const setCached = (userId, data) => {
  profileCache.set(String(userId), { at: Date.now(), data });
  // جلوگیری از رشد بی‌رویه
  if (profileCache.size > 30) {
    const oldestKey = profileCache.keys().next().value;
    profileCache.delete(oldestKey);
  }
};

// ============================================================
// DATA LOADER HOOK
// ============================================================
function useUserProfileData(userId, presenceMap) {
  const { user: currentUser } = useAuth();
  const { exams } = useExams();
  const [state, setState] = useState(() => {
    const cached = userId ? getCached(userId) : null;
    return cached ? { ...cached, loading: false, error: null } : {
      loading: true, error: null,
      role: null, basicInfo: null, teacherProfile: null,
      rewards: { coins: 0, xp: 0 },
      studentStats: null, teacherStats: null,
      learningJourney: [], teachingPortfolio: [],
    };
  });

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    // آیا کش تازه داریم؟
    // برای خودِ کاربر، cache را نادیده بگیر (داده‌ها ممکن است تغییر کرده باشند)
    const isSelf = idsEqual(userId, currentUser?.id);
    const cached = !isSelf ? getCached(userId) : null;
    if (cached) {
      setState({ ...cached, loading: false, error: null });
      return;
    }

    setState((s) => ({ ...s, loading: true, error: null }));

    (async () => {
      try {
        // ─── ۰. تضمین وجود کش سراسری کاربران (اگر از قبل پر نشده) ───
        if (typeof window !== 'undefined' && !Array.isArray(window.__allUsersCache)) {
          try {
            const res = await fetch(`${API_BASE}/users`);
            if (res.ok) {
              window.__allUsersCache = await res.json();
            }
          } catch { /* silent */ }
        }
        if (cancelled) return;

        // ─── ۱. تعیین نقش و اطلاعات پایه از کش ───
        let basicInfo = null;
        if (typeof window !== 'undefined' && Array.isArray(window.__allUsersCache)) {
          basicInfo = window.__allUsersCache.find((u) => u && idsEqual(u.id, userId)) || null;
        }

        // اگر مالک خودمان هستیم
        if (!basicInfo && idsEqual(userId, currentUser?.id)) {
          basicInfo = currentUser;
        }

        // TeacherProfile از registry
        let teacherProfile = null;
        try {
          teacherProfile = dataEngine.teacher.getByUserId(userId) || null;
        } catch { /* ignore */ }

        const isTeacher = !!teacherProfile
          || basicInfo?.role === 'teacher';

        // ─── ۲. Fetch موازی داده‌های سروری (فقط موارد واقعی) ───
        const [rewardsRes, appDataRes] = await Promise.allSettled([
          fetch(`${API_BASE}/userRewards?userId=${encodeURIComponent(userId)}`).then((r) => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API_BASE}/appData`).then((r) => r.ok ? r.json() : null).catch(() => null),
        ]);

        if (cancelled) return;

        // ─── استخراج داده‌ها ───
        const rewardsArr = safeArray(rewardsRes.status === 'fulfilled' ? rewardsRes.value : null);
        const rewards = rewardsArr[0] || null;

        const appData = safeObj(appDataRes.status === 'fulfilled' ? appDataRes.value : null);
        const allCourses = safeArray(appData.courses);

        // ✅ خواندن داده‌های محلی از engine (دقیقاً مثل StudentHome و ParentDashboard)
        //    برای خودِ کاربر همیشه کار می‌کند؛ برای دیگران خالی است (روی سرور نیست)
        const isSelf = idsEqual(userId, currentUser?.id);

        const readLocalItems = (prefix) => {
          try {
            return engine.keys(prefix).map((k) => ({
              key: k,
              data: engine.get(k, {}) || {},
              _userId: userId,
            }));
          } catch {
            return [];
          }
        };

        const lessonProgressList = isSelf
          ? readLocalItems(`lesson-progress-${userId}-`)
          : [];

        const lessonRecordsList = isSelf
          ? [{
              key: `lesson-records-${userId}`,
              data: engine.get(`lesson-records-${userId}`, {}) || {},
              _userId: userId,
            }]
          : [];

        const freestudyProgressList = isSelf
          ? readLocalItems(`freestudy-progress-${userId}-`)
          : [];

        const quizProgressList = isSelf
          ? readLocalItems(`step-quiz-progress-${userId}-`)
          : [];

        // ─── ۳. غنی‌سازی داده‌های basicInfo ───
        const enrichedBasic = {
          id: userId,
          name: getUserDisplayName(basicInfo),
          avatar: basicInfo?.avatar || null,
          grade: basicInfo?.grade || null,
          field: basicInfo?.field || null,
          role: isTeacher ? 'teacher' : (basicInfo?.role || 'student'),
          enrolledCourses: safeArray(basicInfo?.enrolledCourses),
          coins: Number(basicInfo?.coins) || 0,
          xp: Number(basicInfo?.xp) || 0,
        };

        // ─── ۴. Rewards نهایی ───
        const finalRewards = {
          coins: rewards ? Number(rewards.coins) || 0 : enrichedBasic.coins,
          xp: rewards ? Number(rewards.xp) || 0 : enrichedBasic.xp,
        };

        // ─── ۵. League ───
        const league = getDragonForXp(finalRewards.xp);

        // ─── ۶. محاسبه آمار بر اساس نقش ───
        let studentStats = null;
        let teacherStats = null;
        let learningJourney = [];
        let teachingPortfolio = [];

        if (isTeacher) {
          // ═══════════ معلم ═══════════
          teacherStats = computeTeacherStats({
            teacherId: teacherProfile?.id || userId,
            userId,
            exams: safeArray(exams),
            allCourses,
          });
          teachingPortfolio = computeTeachingPortfolio(teacherProfile?.id);
        } else {
          // ═══════════ دانش‌آموز ═══════════
          studentStats = computeStudentStats({
            userId,
            enrolledCourses: enrichedBasic.enrolledCourses,
            exams: safeArray(exams),
            allCourses,
            lessonProgressList,
            lessonRecordsList,
            freestudyProgressList,
          });
          learningJourney = computeLearningJourney({
            userId,
            grade: enrichedBasic.grade,
            field: enrichedBasic.field,
          });
        }

        const result = {
          loading: false,
          error: null,
          role: isTeacher ? 'teacher' : 'student',
          basicInfo: enrichedBasic,
          teacherProfile,
          rewards: finalRewards,
          league,
          studentStats,
          teacherStats,
          learningJourney,
          teachingPortfolio,
          loadedAt: Date.now(),
        };

        if (cancelled) return;
        setCached(userId, result);
        setState(result);
      } catch (e) {
        if (cancelled) return;
        console.error('[UserProfileModal] load failed:', e);
        setState((s) => ({ ...s, loading: false, error: e.message || 'خطا در بارگذاری' }));
      }
    })();

    return () => { cancelled = true; };
  }, [userId, exams, currentUser?.id]);

  return state;
}

// ============================================================
// COMPUTE: آمار دانش‌آموز
// ============================================================
function computeStudentStats({
  userId, enrolledCourses, exams, allCourses,
  lessonProgressList, lessonRecordsList, freestudyProgressList,
}) {
  // ─── آزمون‌های عمومی ───
  const publicExams = exams.filter((e) =>
    e && e.isGlobal === true
    && e.isChallenge !== true
    && e.isStepQuiz !== true
    && e.statusType !== 'archived'
    && safeArray(e.results).some((r) => idsEqual(r.studentId, userId) && r.isFinalSubmit === true)
  ).length;

  // ─── آزمون‌های گروهی ───
  const groupExams = exams.filter((e) =>
    e && !e.isGlobal
    && !e.isChallenge
    && !e.isStepQuiz
    && e.teamId
    && e.statusType !== 'archived'
    && safeArray(e.results).some((r) => idsEqual(r.studentId, userId) && r.isFinalSubmit === true)
  ).length;

  // ─── چالش‌ها ───
  const challenges = exams.filter((e) =>
    e && e.isChallenge === true
    && e.statusType !== 'archived'
    && safeArray(e.results).some((r) => idsEqual(r.studentId, userId) && r.isFinalSubmit === true)
  ).length;

  // ─── کلاس‌های خصوصی برگزارشده ───
  const enrolledSet = new Set(safeArray(enrolledCourses).map(String));
  const privateClasses = allCourses.filter((c) =>
    c && enrolledSet.has(String(c.id)) && c.status === 'ended'
  ).length;

  // ─── ویدیوهای آموزشی دیده‌شده ───
  let edVideos = 0;
  try {
    // ساخت نقشه‌ی lesson-records: compositeId → record
    const recordsByComposite = {};
    for (const rec of lessonRecordsList) {
      if (!rec || !rec.key || !rec.data) continue;
      const uid = rec._userId;
      if (!uid || !idsEqual(uid, userId)) continue;
      if (!rec.key.startsWith('lesson-records-')) continue;
      for (const [cid, r] of Object.entries(rec.data || {})) {
        if (r && typeof r === 'object') recordsByComposite[cid] = r;
      }
    }

    for (const p of lessonProgressList) {
      if (!p || !p.key || !p.data) continue;
      if (p._userId && !idsEqual(p._userId, userId)) continue;
      if (!p.key.startsWith('lesson-progress-')) continue;

      const completedIds = safeArray(p.data.completedStepIds);
      if (completedIds.length === 0) continue;

      // استخراج compositeId از key
      const prefix = `lesson-progress-${userId}-`;
      if (!p.key.startsWith(prefix)) continue;
      const rest = p.key.slice(prefix.length);
      const parts = rest.split('-');
      if (parts.length < 4) continue;
      const lessonId = parts[parts.length - 1];
      const folderName = parts[parts.length - 2];
      const subject = parts[parts.length - 3];
      const grade = parts.slice(0, parts.length - 3).join('-');
      const compositeId = `${grade}|${subject}|${folderName}|${lessonId}`;

      const record = recordsByComposite[compositeId];
      if (!record) continue;

      const lessonStepIds = new Set(safeArray(record.lessonStepIds));
      for (const sid of completedIds) {
        if (lessonStepIds.has(sid)) edVideos++;
      }
    }
  } catch (e) {
    console.warn('[computeStudentStats] edVideos error:', e);
  }

  // ─── ویدیوهای مطالعه آزاد دیده‌شده ───
  let freeVideos = 0;
  try {
    for (const rec of freestudyProgressList) {
      if (!rec) continue;
      const uid = rec._userId;
      if (uid && !idsEqual(uid, userId)) continue;
      const data = rec.data || rec;
      const completed = Array.isArray(data.completedVideoIds)
        ? data.completedVideoIds
        : Array.isArray(data.completedVideos)
          ? data.completedVideos
          : [];
      freeVideos += completed.length;
    }
  } catch (e) {
    console.warn('[computeStudentStats] freeVideos error:', e);
  }

  return {
    publicExams,
    groupExams,
    challenges,
    privateClasses,
    edVideos,
    freeVideos,
  };
}

// ============================================================
// COMPUTE: آمار معلم
// ============================================================
function computeTeacherStats({ teacherId, userId, exams, allCourses }) {
  // ─── آزمون‌های ساخته‌شده (بدون step quiz) ───
  const createdExams = exams.filter((e) =>
    e
    && idsEqual(e.teacherId, userId)
    && e.isStepQuiz !== true
    && e.statusType !== 'archived'
  ).length;

  // ─── آزمون‌های مرحله ───
  const stepQuizzes = exams.filter((e) =>
    e
    && idsEqual(e.teacherId, userId)
    && e.isStepQuiz === true
    && e.statusType !== 'archived'
  ).length;

  // ─── کلاس‌های خصوصی برگزارشده ───
  const privateClasses = allCourses.filter((c) =>
    c && idsEqual(c.teacherId, userId) && c.status === 'ended'
  ).length;

  // ─── ویدیوهای درسی و مطالعه آزاد ───
  let edVideos = 0;
  let freeVideos = 0;
  let publishedProjects = 0;
  let totalBookProjects = 0;
  let totalFreeStudyProjects = 0;

  try {
    const teacherProfileId = teacherId;
    if (teacherProfileId) {
      // کتاب‌ها
      const bookProjects = safeArray(
        dataEngine.project.listByTeacher(teacherProfileId, { type: 'book' })
      );
      totalBookProjects = bookProjects.length;

      for (const p of bookProjects) {
        if (p.publishedVersionId) publishedProjects++;
        const vId = p.publishedVersionId || p.draftVersionId;
        if (!vId) continue;
        const version = dataEngine.version.get(vId);
        if (!version) continue;
        for (const ch of safeArray(version.chapters)) {
          for (const ls of safeArray(ch.lessons)) {
            for (const st of safeArray(ls.steps)) {
              edVideos += safeArray(st.videos).length;
            }
          }
        }
      }

      // مطالعه آزاد
      const freeProjects = safeArray(
        dataEngine.project.listByTeacher(teacherProfileId, { type: 'freeStudy' })
      );
      totalFreeStudyProjects = freeProjects.length;

      for (const p of freeProjects) {
        if (p.publishedVersionId) publishedProjects++;
        const vId = p.publishedVersionId || p.draftVersionId;
        if (!vId) continue;
        const version = dataEngine.version.get(vId);
        if (!version) continue;
        freeVideos += safeArray(version.videos).length;
      }
    }
  } catch (e) {
    console.warn('[computeTeacherStats] projects error:', e);
  }

  return {
    createdExams,
    stepQuizzes,
    privateClasses,
    edVideos,
    freeVideos,
    publishedProjects,
    totalBookProjects,
    totalFreeStudyProjects,
  };
}

// ============================================================
// COMPUTE: سفر یادگیری دانش‌آموز
// ============================================================
// ============================================================
// COMPUTE: سفر یادگیری دانش‌آموز
//   منبع: engine (localStorage) — دقیقاً مثل ParentDashboard / StudentHome
// ============================================================
function computeLearningJourney({ userId, grade, field }) {
  const gradeId = normalizeGradeId(grade);
  if (!gradeId || !COURSE_DETAILS || !userId) return [];

  const fieldId = field ? normalizeFieldId(field) : null;

  // ─── ۱. خواندن مستقیم از engine (بدون واسطه) ───
  //    پشتیبانی از هر دو نام فیلد (completedStepIds / completedSteps)
  const progressIndex = {};
  try {
    const prefix = `lesson-progress-${userId}-`;
    for (const k of engine.keys(prefix)) {
      const p = engine.get(k, {}) || {};
      const ids = Array.isArray(p.completedStepIds)
        ? p.completedStepIds
        : Array.isArray(p.completedSteps)
          ? p.completedSteps
          : [];
      progressIndex[k] = new Set(ids);
    }
  } catch { /* silent */ }

  const records = engine.get(`lesson-records-${userId}`, {}) || {};

  // ─── ۲. فیلتر دوره‌ها بر اساس grade/field ───
  const courses = Object.entries(COURSE_DETAILS).filter(([key]) => {
    if (!key.startsWith(`${gradeId}-`)) return false;
    const parts = key.split('-');
    if (parts.length === 2) return !curriculumNeedsField(gradeId);
    if (parts.length >= 3) {
      const fieldPart = parts[parts.length - 2];
      if (fieldId) return fieldPart === fieldId;
      return true;
    }
    return false;
  });

  // ─── ۳. محاسبه درصد هر درس (union completed + quiz passed — مثل ParentDashboard) ───
  const result = [];
  for (const [key, data] of courses) {
    const parts = key.split('-');
    const subject = parts[parts.length - 1];
    const totalLessons = Number(data.lessons) || 0;
    if (totalLessons <= 0) continue;

    const lessonBest = {};
    for (const rec of Object.values(records)) {
      if (!rec || !rec.subject || rec.subject !== subject) continue;
      if (normalizeGradeId(rec.grade) !== gradeId) continue;

      const total = Number(rec.totalSteps) || 0;
      if (total <= 0) continue;

      const pKey = `lesson-progress-${userId}-${rec.grade}-${rec.subject}-${rec.folderName}-${rec.lessonId}`;
      const completedSet = progressIndex[pKey] || new Set();

      // quiz passed (union) — دقیقاً مثل ParentDashboard
      const passedQuizIds = new Set();
      if (rec.projectId && Array.isArray(rec.quizStepIds) && rec.quizStepIds.length > 0) {
        try {
          const qp = engine.get(`step-quiz-progress-${userId}-${rec.projectId}`, {}) || {};
          rec.quizStepIds.forEach((id) => {
            if (qp[id]?.passed === true) passedQuizIds.add(id);
          });
        } catch { /* silent */ }
      }

      const allDone = new Set([...completedSet, ...passedQuizIds]);
      const done = Math.min(total, allDone.size);
      const ratio = done / total;

      if (!lessonBest[rec.lessonId] || lessonBest[rec.lessonId] < ratio) {
        lessonBest[rec.lessonId] = ratio;
      }
    }

    const sumRatios = Object.values(lessonBest).reduce((s, r) => s + r, 0);
    const percent = Math.min(100, Math.round((sumRatios / totalLessons) * 100));

    result.push({
      key,
      subject,
      title: data.title || subject,
      emoji: SUBJECT_EMOJI[subject] || '📚',
      percent,
      color: colorForSubject(subject),
    });
  }

  return result.sort((a, b) => b.percent - a.percent);
}

// ============================================================
// COMPUTE: پورتفولیوی معلم
// ============================================================
function computeTeachingPortfolio(teacherProfileId) {
  if (!teacherProfileId) return [];

  try {
    const allProjects = [
      ...safeArray(dataEngine.project.listByTeacher(teacherProfileId, { type: 'book' })),
      ...safeArray(dataEngine.project.listByTeacher(teacherProfileId, { type: 'freeStudy' })),
    ];

    const result = allProjects
      .filter((p) => p.publishedVersionId || p.state === 'published')
      .slice(0, 6)
      .map((p) => {
        const vId = p.publishedVersionId || p.draftVersionId;
        let videoCount = 0;
        if (vId) {
          const version = dataEngine.version.get(vId);
          if (version) {
            if (p.type === 'freeStudy') {
              videoCount = safeArray(version.videos).length;
            } else {
              for (const ch of safeArray(version.chapters)) {
                for (const ls of safeArray(ch.lessons)) {
                  for (const st of safeArray(ls.steps)) {
                    videoCount += safeArray(st.videos).length;
                  }
                }
              }
            }
          }
        }
        return {
          id: p.id,
          title: p.title || 'بدون عنوان',
          type: p.type || 'book',
          subject: p.subject || '',
          grade: p.grade || '',
          videoCount,
          state: p.state,
          isPublished: !!p.publishedVersionId,
        };
      });

    return result;
  } catch (e) {
    console.warn('[computeTeachingPortfolio] error:', e);
    return [];
  }
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function UserProfileModal({
  userId,
  onClose,
  presenceMap = {},
  isDarkMode = false,
}) {
  const data = useUserProfileData(userId, presenceMap);
  const modalRef = useRef(null);

  // ESC برای بستن
  useEffect(() => {
    if (!userId) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    // قفل اسکرول
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [userId, onClose]);

  if (!userId) return null;

  const overlayClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  // ─── Avatar Resolution ───
  const [resolvedAvatar, setResolvedAvatar] = useState(() =>
    resolveAvatarUrl(data.basicInfo?.avatar)
  );

  useEffect(() => {
    if (!data.teacherProfile) return;
    const { avatarBlobId, legacyAvatar } = data.teacherProfile;
    if (legacyAvatar) {
      setResolvedAvatar(resolveAvatarUrl(legacyAvatar));
      return;
    }
    if (avatarBlobId) {
      let cancelled = false;
      engine.blobs.getUrl(avatarBlobId).then((url) => {
        if (!cancelled && url) setResolvedAvatar(url);
      }).catch(() => {});
      return () => { cancelled = true; };
    }
  }, [data.teacherProfile?.avatarBlobId, data.teacherProfile?.legacyAvatar]);

  useEffect(() => {
    if (data.basicInfo?.avatar) {
      setResolvedAvatar(resolveAvatarUrl(data.basicInfo.avatar));
    }
  }, [data.basicInfo?.avatar]);

  const userOnline = isOnline(userId, presenceMap);

  const content = (
    <div
      className={`upm-overlay ${isDarkMode ? 'upm-dark' : 'upm-light'}`}
      onClick={overlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="پروفایل کاربر"
    >
      <div className="upm-modal" ref={modalRef}>
        <button
          type="button"
          className="upm-close"
          onClick={onClose}
          aria-label="بستن"
        >
          ✕
        </button>

        {data.loading ? (
          <ProfileSkeleton />
        ) : data.error ? (
          <ProfileError message={data.error} onClose={onClose} />
        ) : (
          <div className="upm-scroll">
            {/* ═══════════ HERO ═══════════ */}
            <div className="upm-hero">
              <div className="upm-avatar-wrap">
                <div className="upm-avatar-ring" />
                <div className="upm-avatar-inner">
                  <img
                    src={resolvedAvatar}
                    alt={data.basicInfo?.name || 'کاربر'}
                    className="upm-avatar-img"
                    onError={(e) => {
                      if (!e.currentTarget.dataset.fb) {
                        e.currentTarget.dataset.fb = '1';
                        e.currentTarget.src = DEFAULT_AVATAR;
                      }
                    }}
                  />
                </div>
                {userOnline && (
                  <span className="upm-online-badge" title="آنلاین" />
                )}
              </div>

              <h2 className="upm-name">{data.basicInfo?.name || 'کاربر'}</h2>

              <div className="upm-badges">
                <span className={`upm-online-text ${userOnline ? 'is-online' : 'is-offline'}`}>
                  {userOnline ? 'آنلاین' : formatLastSeen(userId, presenceMap)}
                </span>

                {data.role === 'teacher' ? (
                  <>
                    <span className="upm-badge upm-badge--teacher">
                      👨‍🏫 معلم
                    </span>
                    {data.teacherProfile?.folderName && (
                      <span className="upm-badge upm-badge--field">
                        🏷️ {data.teacherProfile.folderName}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {data.basicInfo?.grade && (
                      <span className="upm-badge">
                        🎓 {getGrade(normalizeGradeId(data.basicInfo.grade))?.title
                          || data.basicInfo.grade}
                      </span>
                    )}
                    {data.basicInfo?.field
                      && curriculumNeedsField(normalizeGradeId(data.basicInfo.grade)) && (
                        <span className="upm-badge upm-badge--field">
                          📚 {getField(normalizeFieldId(data.basicInfo.field))?.title
                            || data.basicInfo.field}
                        </span>
                      )}
                  </>
                )}
              </div>
            </div>

            {/* ═══════════ WALLET ═══════════ */}
            <div className="upm-wallet">
              <div className="upm-wallet-card upm-wallet-xp">
                <span className="upm-wallet-icon">⚡</span>
                <div className="upm-wallet-info">
                  <div className="upm-wallet-value">
                    {fa(data.rewards.xp.toLocaleString())}
                  </div>
                  <div className="upm-wallet-label">امتیاز (XP)</div>
                </div>
              </div>
              <div className="upm-wallet-card upm-wallet-coins">
                <span className="upm-wallet-icon">🪙</span>
                <div className="upm-wallet-info">
                  <div className="upm-wallet-value">
                    {fa(data.rewards.coins.toLocaleString())}
                  </div>
                  <div className="upm-wallet-label">سکه</div>
                </div>
              </div>
            </div>

            {/* ═══════════ LEAGUE ═══════════ */}
            {data.role === 'student' && <LeagueCard league={data.league} />}

            {/* ═══════════ STATS ═══════════ */}
            {data.role === 'teacher' && data.teacherStats && (
              <TeacherStatsGrid stats={data.teacherStats} />
            )}
            {data.role === 'student' && data.studentStats && (
              <StudentStatsGrid stats={data.studentStats} />
            )}

            {/* ═══════════ JOURNEY / PORTFOLIO ═══════════ */}
            {data.role === 'student' && data.learningJourney.length > 0 && (
              <LearningJourney journey={data.learningJourney} />
            )}

            {data.role === 'teacher' && (
              <>
                {data.teacherProfile && (
                  <TeacherBio
                    profile={data.teacherProfile}
                  />
                )}
                {data.teachingPortfolio.length > 0 && (
                  <TeachingPortfolio projects={data.teachingPortfolio} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return content;
  return createPortal(content, document.body);
}

// ============================================================
// SUB-COMPONENTS
// ============================================================

function LeagueCard({ league }) {
  if (!league || !league.dragon) return null;
  const { dragon, nextDragon, isMax } = league;

  // پیشرفت تا لیگ بعدی
  let progressPct = 100;
  let remainingText = '';
  if (nextDragon) {
    const start = dragon.minScore;
    const end = nextDragon.minScore;
    const current = Math.max(start, Math.min(end, league.dragon.minScore + 1)); // fallback
    // محاسبه‌ی دقیق با xp فعلی نداریم اینجا؛ خودمون می‌خوایم نمایش بدیم
    // اما xp کاربر رو داریم از parent (league از getDragonForXp میاد که xp هم داره)
    // برای سادگی: fill در parent محاسبه میشه
    progressPct = 0; // parent پر می‌کنه
    remainingText = `${fa(Math.max(0, end))} امتیاز دیگر`;
  }

  const startScore = dragon.minScore;
  const nextScore = nextDragon?.minScore;
  // xp فعلی — از league.minScore استفاده می‌کنیم چون xp رو نداریم؛ بهتره از parent پاس بدیم
  // اما برای جلوگیری از تغییرات زیاد، اینجا فقط نمایش می‌دیم

  return (
    <div
      className="upm-league"
      style={{
        '--league-glow': dragon.glow,
        '--league-c1': dragon.c1,
        '--league-c2': dragon.c2,
      }}
    >
      <div className="upm-league-shine" />
      <div className="upm-league-dragon">
        {dragon.image ? (
          <img
            src={dragon.image}
            alt={dragon.name}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const next = e.currentTarget.nextSibling;
              if (next) next.style.display = 'flex';
            }}
          />
        ) : null}
        <span
          className="dragon-emoji"
          style={{ display: dragon.image ? 'none' : 'flex' }}
          aria-hidden="true"
        >
          {dragon.emoji}
        </span>
      </div>
      <div className="upm-league-body">
        <div className="upm-league-title">لیگ فعلی</div>
        <div className="upm-league-name">{dragon.name}</div>
        <div className="upm-league-progress-track">
          <div
            className="upm-league-progress-fill"
            style={{
              width: nextDragon
                ? `${Math.max(4, Math.min(100, 40))}%`
                : '100%',
            }}
          />
        </div>
        <div className="upm-league-next">
          {isMax
            ? '🏆 به بالاترین لیگ رسیدی!'
            : nextDragon
              ? `تا لیگ «${nextDragon.name}»`
              : '—'}
        </div>
      </div>
    </div>
  );
}

function StudentStatsGrid({ stats }) {
  const items = [
    { icon: '📝', value: stats.publicExams, label: 'آزمون عمومی', type: 'public' },
    { icon: '👥', value: stats.groupExams, label: 'آزمون گروهی', type: 'group' },
    { icon: '⚔️', value: stats.challenges, label: 'چالش', type: 'challenge' },
    { icon: '🎓', value: stats.privateClasses, label: 'کلاس خصوصی', type: 'class' },
    { icon: '🎬', value: stats.edVideos, label: 'ویدیوی آموزشی', type: 'video-ed' },
    { icon: '📺', value: stats.freeVideos, label: 'ویدیوی آزاد', type: 'video-fs' },
  ];

  return (
    <>
      <div className="upm-section-title">
        <span>📊</span>
        <span>آمار فعالیت</span>
      </div>
      <div className="upm-stats">
        {items.map((it) => (
          <div key={it.type} className="upm-stat" data-type={it.type}>
            <span className="upm-stat-icon">{it.icon}</span>
            <div className="upm-stat-value">{fa(it.value)}</div>
            <div className="upm-stat-label">{it.label}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function TeacherStatsGrid({ stats }) {
  const items = [
    { icon: '📝', value: stats.createdExams, label: 'آزمون ساخته‌شده', type: 'public' },
    { icon: '🎯', value: stats.stepQuizzes, label: 'آزمون مرحله', type: 'group' },
    { icon: '🎓', value: stats.privateClasses, label: 'کلاس برگزارشده', type: 'class' },
    { icon: '🎬', value: stats.edVideos, label: 'ویدیوی درسی', type: 'video-ed' },
    { icon: '📺', value: stats.freeVideos, label: 'ویدیوی آزاد', type: 'video-fs' },
    { icon: '📚', value: stats.publishedProjects, label: 'پروژه منتشرشده', type: 'challenge' },
  ];

  return (
    <>
      <div className="upm-section-title">
        <span>📊</span>
        <span>آمار محتوا</span>
      </div>
      <div className="upm-stats">
        {items.map((it) => (
          <div key={it.type} className="upm-stat" data-type={it.type}>
            <span className="upm-stat-icon">{it.icon}</span>
            <div className="upm-stat-value">{fa(it.value)}</div>
            <div className="upm-stat-label">{it.label}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function LearningJourney({ journey }) {
  return (
    <>
      <div className="upm-section-title">
        <span>🗺️</span>
        <span>سفر یادگیری</span>
      </div>
      <div className="upm-journey">
        {journey.map((item) => (
          <div key={item.key} className="upm-journey-item">
            <div className="upm-journey-head">
              <span className="upm-journey-name">
                <span className="upm-journey-emoji">{item.emoji}</span>
                <span>{item.title}</span>
              </span>
              <span className="upm-journey-percent">{fa(item.percent)}٪</span>
            </div>
            <div className="upm-journey-bar">
              <div
                className="upm-journey-fill"
                style={{
                  width: `${item.percent}%`,
                  '--bar-c1': item.color,
                  '--bar-c2': item.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function TeacherBio({ profile }) {
  if (!profile) return null;
  const { bio, experienceYears, specialties } = profile;

  if (!bio && !experienceYears && (!specialties || specialties.length === 0)) {
    return null;
  }

  return (
    <>
      <div className="upm-section-title">
        <span>👨‍🏫</span>
        <span>درباره معلم</span>
      </div>
      <div className="upm-bio-card">
        {experienceYears > 0 && (
          <div className="upm-bio-row">
            <span className="upm-bio-icon">💼</span>
            <span>{fa(experienceYears)} سال سابقه تدریس</span>
          </div>
        )}
        {bio && (
          <p className="upm-bio-text">{bio}</p>
        )}
        {Array.isArray(specialties) && specialties.length > 0 && (
          <div className="upm-bio-specialties">
            {specialties.map((s, i) => (
              <span key={i} className="upm-bio-chip">{s}</span>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function TeachingPortfolio({ projects }) {
  return (
    <>
      <div className="upm-section-title">
        <span>📚</span>
        <span>محتوای منتشرشده</span>
      </div>
      <div className="upm-portfolio">
        {projects.map((p) => (
          <div key={p.id} className="upm-portfolio-item">
            <div className="upm-portfolio-emoji">
              {p.type === 'freeStudy' ? '🎬' : (SUBJECT_EMOJI[p.subject] || '📘')}
            </div>
            <div className="upm-portfolio-info">
              <div className="upm-portfolio-title">{p.title}</div>
              <div className="upm-portfolio-meta">
                {p.type === 'freeStudy' ? 'مطالعه آزاد' : 'کتاب درسی'}
                {p.videoCount > 0 && ` · ${fa(p.videoCount)} ویدیو`}
              </div>
            </div>
            {p.isPublished && (
              <span className="upm-portfolio-badge">✓ منتشرشده</span>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function ProfileSkeleton() {
  return (
    <div className="upm-scroll">
      <div className="upm-hero">
        <div className="upm-skel upm-skel-avatar" />
        <div className="upm-skel upm-skel-name" />
        <div className="upm-skel upm-skel-badge" />
      </div>
      <div className="upm-wallet">
        <div className="upm-skel upm-skel-card" />
        <div className="upm-skel upm-skel-card" />
      </div>
      <div className="upm-skel upm-skel-league" />
      <div className="upm-skel upm-skel-grid" />
    </div>
  );
}

function ProfileError({ message, onClose }) {
  return (
    <div className="upm-error">
      <div className="upm-error-icon">⚠️</div>
      <h3>خطا در بارگذاری پروفایل</h3>
      <p>{message}</p>
      <button type="button" className="upm-error-btn" onClick={onClose}>
        بستن
      </button>
    </div>
  );
}